import { useState, useCallback, type ReactNode } from "react";
import { CToast, CToastBody, CToastClose, CToaster } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import {
  cilCheckCircle,
  cilWarning,
  cilXCircle,
  cilInfo,
} from "@coreui/icons";
import {
  ToastContext,
  type ToastAction,
  type ToastContextValue,
} from "./useToast";

type ToastColor = "success" | "danger" | "warning" | "info";

interface ToastEntry {
  id: number;
  message: string;
  color: ToastColor;
  action?: ToastAction;
}

const TOAST_DURATION_MS = 4000;

const iconForColor = (color: ToastColor) => {
  switch (color) {
    case "success":
      return cilCheckCircle;
    case "danger":
      return cilXCircle;
    case "warning":
      return cilWarning;
    case "info":
      return cilInfo;
  }
};

interface ProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const add = useCallback(
    (message: string, color: ToastColor, action?: ToastAction) => {
      const id = Date.now() + Math.random();
      setToasts((prev) => [...prev, { id, message, color, action }]);
      setTimeout(() => remove(id), TOAST_DURATION_MS);
    },
    [remove]
  );

  const value: ToastContextValue = {
    showSuccess: (m, a) => add(m, "success", a),
    showError: (m, a) => add(m, "danger", a),
    showWarning: (m, a) => add(m, "warning", a),
    showInfo: (m, a) => add(m, "info", a),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <CToaster placement="bottom-center" className="position-fixed p-3">
        {toasts.map((toast) => (
          <CToast
            key={toast.id}
            visible
            color={toast.color}
            className="text-white align-items-center"
          >
            <div className="d-flex">
              <CToastBody className="d-flex align-items-center">
                <CIcon
                  icon={iconForColor(toast.color)}
                  className="flex-shrink-0 me-2"
                  width={20}
                  height={20}
                />
                {toast.message}
                {toast.action && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-white fw-semibold ms-3 p-0 text-decoration-underline"
                    onClick={() => {
                      toast.action!.onClick();
                      // Dismissed on use: leaving an already-taken undo on screen invites a second
                      // click that would undo the undo.
                      remove(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </button>
                )}
              </CToastBody>
              <CToastClose
                className="me-2 m-auto"
                white
                onClick={() => remove(toast.id)}
              />
            </div>
          </CToast>
        ))}
      </CToaster>
    </ToastContext.Provider>
  );
}

